/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  ConditionData,
  ConditionFieldProps,
  DEFAULT_TIMEZONE,
} from "@advapacs/common";
import {
  BETWEEN,
  NOT_BETWEEN,
  GREATER,
  GREATER_OR_EQUAL,
  LESS,
  LESS_OR_EQUAL,
  TIME_OF_DAY,
  getSelectStyles,
} from "../../../constant";
import TimezoneSelect, { allTimezones } from "react-timezone-select";

const Time: FunctionComponent<ConditionFieldProps> = ({
  disabled,
  id,
  parent,
  errors,
  condition,
  isHasScope = true,
  onValueChangeHandler,
  isDark,
}: ConditionFieldProps) => {
  const { t } = useTranslation("rule");

  const dispatchNewValue = (con: ConditionData) => {
    onValueChangeHandler(
      {
        type: TIME_OF_DAY,
        scope: isHasScope ? "conditions" : undefined,
        id,
        timeOfDay: con,
      },
      parent
    );
  };

  const updateValue = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      ...condition,
      value: event.currentTarget.value,
    });
  };

  const updateFrom = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      ...condition,
      from: event.currentTarget.value,
    });
  };

  const updateTo = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      ...condition,
      to: event.currentTarget.value,
    });
  };

  const selectOperator = (update: any) => {
    dispatchNewValue({ ...condition, operator: update?.value });
  };

  const options = [
    { label: t("greater"), value: GREATER },
    { label: t("greater_or_equal"), value: GREATER_OR_EQUAL },
    { label: t("less"), value: LESS },
    { label: t("less_or_equal"), value: LESS_OR_EQUAL },
    { label: t("between"), value: BETWEEN },
    { label: t("not_between"), value: NOT_BETWEEN },
  ];

  const hasError = () => {
    return (
      errors.find((item) => item.id === id && item.type === TIME_OF_DAY) !==
      undefined
    );
  };

  return (
    <div className="row">
      <div className="col-md-3 col-sm-12">
        <div className="mb-1">
          <TimezoneSelect
            name="timeZone"
            styles={getSelectStyles(isDark as boolean)}
            onChange={(val) => {
              dispatchNewValue({
                ...condition,
                timeZone: val.value,
              });
            }}
            timezones={allTimezones}
            value={condition?.timeZone || DEFAULT_TIMEZONE.value}
            isSearchable
            isDisabled={disabled}
          />
        </div>
      </div>
      <div className="col-md-3 col-sm-12">
        <div className="mb-1">
          <Select
            menuPlacement="auto"
            menuPosition="fixed"
            styles={getSelectStyles(isDark as boolean)}
            isDisabled={disabled}
            id={`${id}_operator`}
            name="operator"
            classNamePrefix="form-control-select"
            options={options}
            placeholder={t("select_operator")}
            onChange={selectOperator}
            value={options.find(
              (option) => option?.value === condition?.operator
            )}
          />
          {hasError() && !condition?.operator && (
            <label
              className="validation-invalid-label"
              htmlFor={`${id}_operator`}
            >
              {t("field_required")}
            </label>
          )}
        </div>
      </div>
      {condition?.operator &&
        [LESS, LESS_OR_EQUAL, GREATER, GREATER_OR_EQUAL].includes(
          condition?.operator
        ) && (
          <div className="col-md-3 col-sm-12">
            <input
              disabled={disabled}
              type="time"
              id={`${id}_value`}
              name="value"
              className="form-control mb-1"
              placeholder={t("value")}
              onChange={updateValue}
              value={condition?.value}
            />
            {hasError() && !condition?.value && (
              <label
                className="validation-invalid-label"
                htmlFor={`${id}_value`}
              >
                {t("field_required")}
              </label>
            )}
          </div>
        )}
      {condition?.operator &&
        [BETWEEN, NOT_BETWEEN].includes(condition?.operator) && (
          <>
            <div className="col-md-3 col-sm-12">
              <input
                disabled={disabled}
                type="time"
                id={`${id}_from`}
                name="from"
                className="form-control mb-1"
                value={condition?.from}
                placeholder={t("from")}
                onChange={updateFrom}
              />
              {hasError() && !condition?.from && (
                <label
                  className="validation-invalid-label"
                  htmlFor={`${id}_from`}
                >
                  {t("field_required")}
                </label>
              )}
            </div>
            <div className="col-md-3 col-sm-12">
              <input
                disabled={disabled}
                type="time"
                id={`${id}_to`}
                name="to"
                className="form-control mb-1"
                value={condition?.to}
                placeholder={t("to")}
                onChange={updateTo}
              />
              {hasError() && !condition?.to && (
                <label
                  className="validation-invalid-label"
                  htmlFor={`${id}_to`}
                >
                  {t("field_required")}
                </label>
              )}
            </div>
          </>
        )}
    </div>
  );
};

export default Time;
